import React from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import SEO from "../seo/SEO";
import Section from "../Components/ui/Section";
import Button from "../Components/ui/Button";
import { Accent, Display, Label } from "../Components/ui/Type";
import { PRIMARY_NAV } from "../Components/layout/navigation";
import { color, font, motion, space, type } from "../design/tokens";

/**
 * 404.
 *
 * Marked `noindex`, and it offers the actual routes rather than dumping the
 * visitor at the homepage. The old site sent unknown URLs to a page titled
 * "403" showing a stock illustration, which was both the wrong status and a
 * dead end.
 */
const NotFoundPage = () => (
  <>
    <SEO
      title="Page not found"
      description="That page doesn't exist. Here's where to go instead."
      path="/404"
      noindex
    />

    <Box
      component="section"
      sx={{
        backgroundColor: color.ink,
        color: color.onInk,
        paddingTop: { xs: "128px", md: "200px" },
        paddingBottom: { xs: 10, md: 16 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: space.maxWidth,
          marginInline: "auto",
          paddingInline: space.gutter,
        }}
      >
        <Label tone="onInk">Error 404</Label>
        <Display as="h1" size="xl" tone="onInk" sx={{ marginTop: 3, maxWidth: 900 }}>
          That page isn't <Accent sx={{ color: color.clayLight }}>here</Accent>.
        </Display>
        <Box
          component="p"
          sx={{ ...type.lead, color: color.onInkMute, marginTop: 3, maxWidth: 520 }}
        >
          The link may be out of date, or the address may have a typo in it.
          Everything on the site is one of these:
        </Box>

        <Box
          component="ul"
          sx={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            marginTop: 5,
            borderTop: `1px solid ${color.lineOnInk}`,
            maxWidth: 640,
          }}
        >
          {PRIMARY_NAV.map((item) => (
            <Box component="li" key={item.to}>
              <Box
                component={Link}
                to={item.to}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  paddingBlock: 2.25,
                  borderBottom: `1px solid ${color.lineOnInk}`,
                  fontFamily: font.display,
                  fontSize: "1.5rem",
                  color: color.onInk,
                  transition: `color ${motion.base} ${motion.ease}`,
                  "&:hover": { color: color.clayLight },
                }}
              >
                {item.label}
                <Box component="span" aria-hidden="true" sx={{ color: color.clayLight }}>
                  →
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Button to="/" variant="onInk" size="lg">
            Back to the homepage
          </Button>
          <Button to="/contact" variant="onInkGhost" size="lg">
            Get a Free Quote
          </Button>
        </Box>
      </Box>
    </Box>

    <Section tone="paper" size="tight">
      <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0 }}>
        If you followed a link here from elsewhere on the site, we'd like to fix
        it — let us know at{" "}
        <Box
          component="a"
          href="mailto:Fivelightsreno@hotmail.com"
          sx={{ color: color.clay, textDecoration: "underline" }}
        >
          Fivelightsreno@hotmail.com
        </Box>
        .
      </Box>
    </Section>
  </>
);

export default NotFoundPage;
